import { useLazyQuery } from '@apollo/client';
import { opportunitiesListApi } from '../api';
import { formatFilterForPayload, formatOpportunityListToTableData } from '../lib';
import { APIRequestPagination, APIRequestSort, Nullable } from 'global';
import { VOFilters } from 'modules/vendorOpportunity/filters';
import { useDebounce } from 'hooks/useDebounce';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Account } from '@trnsact/trnsact-shared-types';

export function useOpportunitiesListData(
  currentFilterConfig: Nullable<Partial<VOFilters>>,
  searchTerm: string,
  pagination: APIRequestPagination,
  sorting?: APIRequestSort
) {
  const account = useSelector((state: any) => state.account) as Account;

  const debouncedSearchTerm = useDebounce(searchTerm);

  const [getVendorOpportunitiesList, { data: vendorOpportunitiesListData, loading: isOpportunityListloading }] =
    useLazyQuery(opportunitiesListApi.getVendorOpportunitiesList, {
      notifyOnNetworkStatusChange: true,
    });

  const [
    getVendorOpportunitiesListPartner,
    { data: vendorOpportunitiesListPartnerData, loading: isOpportunityListPartnerloading },
  ] = useLazyQuery(opportunitiesListApi.getVendorOpportunitiesListPartner, {
    notifyOnNetworkStatusChange: true,
  });

  const isPartner = account.accountType === 'Partner';

  const input = useMemo(() => {
    return {
      offset: (pagination.page - 1) * pagination.perPage,
      limit: pagination.perPage,
      filters: formatFilterForPayload(currentFilterConfig),
      searchTerm: debouncedSearchTerm.trim().length > 2 ? debouncedSearchTerm : undefined,
      sorting,
    };
  }, [currentFilterConfig, debouncedSearchTerm, pagination, sorting]);

  useEffect(() => {
    if (isPartner) {
      getVendorOpportunitiesListPartner({ variables: { input } });
    } else {
      getVendorOpportunitiesList({ variables: { input } });
    }
  }, [input, getVendorOpportunitiesList, getVendorOpportunitiesListPartner]);

  const opportunityListData = isPartner
    ? vendorOpportunitiesListPartnerData?.vendorOpportunitiesListPartner?.data
    : vendorOpportunitiesListData?.vendorOpportunitiesList?.data;

  const opportunityListTotal = isPartner
    ? vendorOpportunitiesListPartnerData?.vendorOpportunitiesListPartner?.total
    : vendorOpportunitiesListData?.vendorOpportunitiesList?.total;

  return {
    isLoading: isOpportunityListloading || isOpportunityListPartnerloading,
    opportunityList: opportunityListData ? formatOpportunityListToTableData(opportunityListData) : [],
    total: opportunityListTotal ?? 0,
  };
}
